/* Created by Interactive 63 - https://interactive63.com */
/**
 * Path interpolation and formatting shared by the editor and the runtime. It has no
 * dependencies and is written to create as little garbage as possible, because it runs on every
 * animation frame.
 */

export interface MorphTemplate {
  /** One entry per subpath: how many cubic segments it has, and whether it is closed. */
  subpaths: { segments: number; closed: boolean }[];
}

/** Writes `a + (b - a) * p` for every number into `out`, which is reused between frames. */
export function lerpValues(
  a: readonly number[],
  b: readonly number[],
  p: number,
  out: number[],
): number[] {
  for (let i = 0; i < a.length; i += 1) {
    const from = a[i] as number;
    out[i] = from + ((b[i] as number) - from) * p;
  }
  return out;
}

/** Trims a number to three decimals without trailing zeros. */
function trim(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

/**
 * Formats values as path data: `M x y C … Z` per subpath. The values are laid out as, for each
 * subpath, the start x and y followed by six numbers per segment.
 */
export function formatPath(template: MorphTemplate, values: readonly number[]): string {
  const parts: string[] = [];
  let i = 0;
  for (const subpath of template.subpaths) {
    parts.push(`M${trim(values[i] as number)} ${trim(values[i + 1] as number)}`);
    i += 2;
    for (let s = 0; s < subpath.segments; s += 1, i += 6) {
      parts.push(
        `C${trim(values[i] as number)} ${trim(values[i + 1] as number)} ${trim(values[i + 2] as number)} ${trim(values[i + 3] as number)} ${trim(values[i + 4] as number)} ${trim(values[i + 5] as number)}`,
      );
    }
    if (subpath.closed) parts.push('Z');
  }
  return parts.join('');
}
